import React, { useContext, useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import {
  FEATURED_ITEM_ENTITY_TYPE,
  FEATURED_ITEM_LINK_TYPE,
} from "common/constants";
import type { UpdateFeaturedItemInput } from "common/utils/featuredContent";
import { useGenerateImagePresignedUrlMutation } from "../../../../generated/graphql";
import { useStatusToasts } from "../../../../hooks/useStatusToasts";
import { spacing } from "../../../../stitches.config";
import { AuthContext } from "../../../Authorization/AuthContext";
import { FlexColumn, FlexRow } from "../../../Common/__styles__/Layout";
import {
  ButtonSection,
  Container,
  ContentSection,
  FormSection,
  HeaderSection,
  InputRow,
  PrimaryButtons,
} from "../../../Common/__styles__/Modal";
import { Button } from "../../../Common/Button";
import Modal from "../../../Common/Modal";
import { ReactHookFormImageUpload } from "../../../Inputs/ImageUpload";
import { Text, Textarea } from "../../../Inputs/react-hook-form";
import Select from "../../../Inputs/Select";
import type { FeaturedItemWithImage } from "./featuredContentInputReducer";
import type { FeaturedItemInput } from "./FeaturedContentSectionsInput";
import { getPublicSubmissionTypes } from "./utils";

export type FeaturedItemModalProps = {
  hide: () => void;
  onSave: (data: FeaturedItemInput) => void;
  initialData?: Partial<UpdateFeaturedItemInput>;
  existingImageUrl?: string;
  existingImageFilename?: string;
  existingImageMimeType?: string;
  modalTitle: string;
  buttonLabel: string;
};

type SelectedContent = {
  entityType: FEATURED_ITEM_ENTITY_TYPE;
  entityId: string;
  linkType: Maybe<FEATURED_ITEM_LINK_TYPE>;
};

interface FeaturedItemFormStructure {
  image: { blob: File; originalFilename: string };
  imageAccessibilityText: string;
  title: string;
  description: string;
  selectedContent: string;
  linkText: string;
}

type ContentOption = {
  value: string;
  label: string;
};

type GroupedContentOptions = Array<{
  label: string;
  options: Array<ContentOption>;
}>;

export const FeaturedItemModal = ({
  hide,
  onSave,
  initialData,
  existingImageUrl,
  existingImageFilename,
  existingImageMimeType,
  modalTitle,
  buttonLabel,
}: FeaturedItemModalProps) => {
  const { account } = useContext(AuthContext);

  const groupedOptions = useMemo((): GroupedContentOptions => {
    const options: GroupedContentOptions = [];

    if (!account) {
      return options;
    }

    const savedViews = (account.savedViews ?? []).filter(
      view => !view.hiddenFromPublic,
    );

    const viewsOptions: Array<ContentOption> = savedViews
      .filter(view => view.id)
      .map(view => ({
        value: JSON.stringify({
          entityType: FEATURED_ITEM_ENTITY_TYPE.SAVED_VIEW,
          entityId: view.id,
          linkType: null,
        } as SelectedContent),
        label: view.name,
      }));

    if (viewsOptions.length > 0) {
      options.push({
        label: "Views",
        options: viewsOptions,
      });
    }

    const mapOptions: Array<ContentOption> = savedViews
      .filter(view => view.id)
      .map(view => ({
        value: JSON.stringify({
          entityType: FEATURED_ITEM_ENTITY_TYPE.SAVED_VIEW,
          entityId: view.id,
          linkType: FEATURED_ITEM_LINK_TYPE.MAP_LAYER,
        } as SelectedContent),
        label: view.name,
      }));

    if (mapOptions.length > 0) {
      options.push({
        label: "Map Layers",
        options: mapOptions,
      });
    }

    const publicSubmissionTypes = getPublicSubmissionTypes({
      submissionTypes: account?.submissionTypes,
      accountDocumentTypes: account?.accountDocumentTypes,
    });

    if (publicSubmissionTypes.length > 0) {
      const submissionTypeOptions: Array<ContentOption> =
        publicSubmissionTypes.map(submissionType => ({
          value: JSON.stringify({
            entityType: FEATURED_ITEM_ENTITY_TYPE.SUBMISSION_TYPE,
            entityId: submissionType.id,
            linkType: null,
          } as SelectedContent),
          label: submissionType.name,
        }));

      options.push({
        label: "Submit new record",
        options: submissionTypeOptions,
      });
    }

    const workflowOptions: Array<ContentOption> = (
      account.workflowDefinitions ?? []
    ).map(workflow => ({
      value: JSON.stringify({
        entityType: FEATURED_ITEM_ENTITY_TYPE.WORKFLOW_DEFINITION,
        entityId: workflow.id,
        linkType: null,
      } as SelectedContent),
      label: workflow.name,
    }));

    if (workflowOptions.length > 0) {
      options.push({
        label: "Workflows",
        options: workflowOptions,
      });
    }

    return options;
  }, [account]);

  const initialSelectedContent = useMemo<string>(() => {
    if (initialData?.entityType && initialData?.entityId) {
      const content: SelectedContent = {
        entityType: initialData.entityType,
        entityId: initialData.entityId,
        linkType: initialData.linkType ?? null,
      };
      return JSON.stringify(content);
    }
    return "";
  }, [initialData]);

  const initialImage = useMemo(() => {
    if (existingImageUrl && existingImageFilename) {
      const placeholderBlob = Object.assign(
        new File([], existingImageFilename, { type: "image/jpeg" }),
        { url: existingImageUrl },
      );
      return {
        blob: placeholderBlob,
        originalFilename: existingImageFilename,
      };
    }
    return undefined;
  }, [existingImageUrl, existingImageFilename]);

  const form = useForm<FeaturedItemFormStructure>({
    defaultValues: {
      image: initialImage,
      imageAccessibilityText: initialData?.imageAccessibilityText || "",
      title: initialData?.title || "",
      description: initialData?.description || "",
      selectedContent: initialSelectedContent,
      linkText: initialData?.linkText || "",
    },
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = form;

  const { addErrorToast } = useStatusToasts();
  const [isUploading, setIsUploading] = useState(false);
  const [generateImagePresignedUrl] = useGenerateImagePresignedUrlMutation();

  const handleSave = async (formData: FeaturedItemFormStructure) => {
    if (!formData.selectedContent) {
      return;
    }

    const selectedContent: SelectedContent = JSON.parse(
      formData.selectedContent,
    );

    const isNewImage =
      formData.image?.blob &&
      formData.image.blob instanceof File &&
      formData.image.blob.size > 0;

    if (!isNewImage && existingImageFilename) {
      onSave({
        imageFilename: existingImageFilename,
        imageMimeType: existingImageMimeType,
        imageAccessibilityText: formData.imageAccessibilityText,
        title: formData.title,
        description: formData.description,
        entityType: selectedContent.entityType,
        entityId: selectedContent.entityId,
        linkType: selectedContent.linkType ?? undefined,
        linkText: formData.linkText,
        displayOrder: initialData?.displayOrder ?? 0,
        imageBlobUrl: existingImageUrl,
      });
      hide();
      return;
    }

    if (!formData.image?.blob) {
      return;
    }

    setIsUploading(true);
    try {
      const mimeType = formData.image.blob.type || "image/jpeg";

      const { data: urlData } = await generateImagePresignedUrl({
        variables: {
          data: {
            clientMimeType: mimeType,
            clientKey: crypto.randomUUID(),
          },
        },
      });

      if (!urlData?.generateImagePresignedUrl) {
        addErrorToast("An unexpected error occurred. Please try again.");
        setIsUploading(false);
        return;
      }

      const { presignedUrl, scratchId } = urlData.generateImagePresignedUrl;

      const uploadResponse = await fetch(presignedUrl, {
        method: "PUT",
        body: formData.image.blob,
        headers: {
          "Content-Type": mimeType,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error("An unexpected error occurred. Please try again.");
      }

      onSave({
        imageFilename: scratchId,
        imageMimeType: mimeType,
        imageAccessibilityText: formData.imageAccessibilityText,
        title: formData.title,
        description: formData.description,
        entityType: selectedContent.entityType,
        entityId: selectedContent.entityId,
        linkType: selectedContent.linkType ?? undefined,
        linkText: formData.linkText,
        displayOrder: initialData?.displayOrder ?? 0,
        imageBlobUrl: (formData.image.blob as File & { url?: string }).url,
      });
      hide();
    } catch (error) {
      addErrorToast(
        error instanceof Error ?
          error.message
        : "An unexpected error occurred. Please try again.",
      );
      setIsUploading(false);
    }
  };

  return (
    <Container width="wide">
      <HeaderSection>
        <h1>{modalTitle}</h1>
        <h2>Homepage highlights</h2>
      </HeaderSection>
      <FormProvider {...form}>
        <FormSection
          onSubmit={handleSubmit(async userProvidedData => {
            await handleSave(userProvidedData);
          })}
          style={{
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            marginBottom: 0,
          }}
        >
          <ContentSection
            data-testid="inputs"
            style={{
              overflow: "hidden",
              display: "flex",
              flex: 1,
              minHeight: 0,
              padding: spacing.l.value,
            }}
          >
            <FlexRow
              style={{
                gap: spacing.l.value,
                alignItems: "flex-start",
                flex: 1,
                minHeight: 0,
              }}
            >
              <FlexColumn
                style={{
                  flex: 1,
                  gap: spacing.m.value,
                  overflowY: "auto",
                  maxHeight: "100%",
                }}
              >
                <InputRow hasError={!!errors.selectedContent}>
                  <Controller
                    control={control}
                    name="selectedContent"
                    rules={{ required: "Select content is required" }}
                    render={({ field, fieldState }) => {
                      return (
                        <Select<string, ContentOption, false>
                          name="selectedContent"
                          required
                          value={field.value}
                          error={fieldState.error?.message}
                          label="Select content"
                          options={groupedOptions}
                          onChange={field.onChange}
                        />
                      );
                    }}
                  />
                </InputRow>
                <InputRow hasError={!!errors.title}>
                  <Text
                    label="Title"
                    error={errors.title?.message}
                    {...register("title", {
                      required: "Title is required",
                    })}
                    required
                  />
                </InputRow>
                <InputRow hasError={!!errors.description}>
                  <Textarea
                    label="Description"
                    error={errors.description?.message}
                    helperText={
                      !errors.description ? "200 characters max" : undefined
                    }
                    {...register("description", {
                      required: "Description is required",
                      maxLength: {
                        value: 200,
                        message: "Description must be 200 characters or less",
                      },
                    })}
                    required
                  />
                </InputRow>
                <InputRow hasError={!!errors.linkText}>
                  <Text
                    label="Link text"
                    error={errors.linkText?.message}
                    {...register("linkText", {
                      required: "Link text is required",
                    })}
                    required
                  />
                </InputRow>
              </FlexColumn>
              <FlexColumn
                style={{
                  flex: 1,
                  gap: spacing.m.value,
                  overflow: "visible",
                }}
              >
                <InputRow>
                  <ReactHookFormImageUpload
                    name="image"
                    control={control}
                    label="Image"
                    required
                    rules={{
                      required: "Image is required",
                    }}
                    skipPlaceholderValidation={!!existingImageFilename}
                  />
                </InputRow>
                <InputRow hasError={!!errors.imageAccessibilityText}>
                  <Text
                    label="Alternative text"
                    error={errors.imageAccessibilityText?.message}
                    {...register("imageAccessibilityText", {
                      required: "Alternative text is required",
                    })}
                    required
                  />
                </InputRow>
              </FlexColumn>
            </FlexRow>
          </ContentSection>
          <ButtonSection>
            <PrimaryButtons>
              <Button
                styleVariant="secondary"
                type="button"
                onClick={() => {
                  hide();
                }}
                size="medium"
              >
                Cancel
              </Button>
              <Button
                styleVariant="primary"
                type="submit"
                disabled={Object.keys(errors).length > 0 || isUploading}
                size="medium"
              >
                {isUploading ? "Uploading..." : buttonLabel}
              </Button>
            </PrimaryButtons>
          </ButtonSection>
        </FormSection>
      </FormProvider>
    </Container>
  );
};

type AddFeaturedItemModalHookProps = {
  onAdd: (data: FeaturedItemInput) => void;
};

export const useAddFeaturedItemModal = ({
  onAdd,
}: AddFeaturedItemModalHookProps) => {
  const [show, hide] = useModal(
    () => (
      <Modal onRequestClose={() => hide()}>
        <FeaturedItemModal
          onSave={onAdd}
          hide={hide}
          modalTitle="Add featured content"
          buttonLabel="Add"
        />
      </Modal>
    ),
    [onAdd],
  );

  return [show, hide] as const;
};

type EditFeaturedItemModalHookProps = {
  item: FeaturedItemWithImage;
  onUpdate: (data: FeaturedItemInput) => void;
};

export const useEditFeaturedItemModal = ({
  item,
  onUpdate,
}: EditFeaturedItemModalHookProps) => {
  const [show, hide] = useModal(() => {
    const { image, ...initialData } = item;
    return (
      <Modal onRequestClose={() => hide()}>
        <FeaturedItemModal
          onSave={onUpdate}
          hide={hide}
          modalTitle="Edit featured content"
          buttonLabel="Save"
          initialData={{
            ...initialData,
            linkType: item.linkType ?? undefined,
          }}
          existingImageUrl={item.image.url}
          existingImageFilename={item.imageFilename}
          existingImageMimeType={item.imageMimeType}
        />
      </Modal>
    );
  }, [onUpdate, item]);

  return [show, hide] as const;
};
